import { LitElement, html, css, type TemplateResult } from "lit";
import { customElement, property, query } from "lit/decorators.js";
import { msg, localized } from "@lit/localize";
import { debounce } from "debounce";

declare global {
  interface HTMLElementTagNameMap {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    "web-kiosk-back-button": WebKioskBackButtonElement;
  }
}

@localized()
@customElement("web-kiosk-back-button")
export class WebKioskBackButtonElement extends LitElement {
  @property({ type: Number }) debounceTime = 100;

  @property({ type: Number }) showAfterPx = 250;

  @query("button") backButton: HTMLElement | undefined;

  connectedCallback(): void {
    super.connectedCallback();

    document.addEventListener("scroll", this.debounceToggleVisibility());
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();

    document.removeEventListener("scroll", this.debounceToggleVisibility());
  }

  debounceToggleVisibility(): () => void {
    return debounce(() => {
      this.toggleButtonVisibility(this);
    }, this.debounceTime);
  }

  toggleButtonVisibility(context: WebKioskBackButtonElement) {
    const show = window.scrollY >= context.showAfterPx;

    this.backButton?.classList.toggle("visible", show);
  }

  scrollToTop() {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  render(): TemplateResult {
    return html`<button id="go-back" @click=${this.scrollToTop}>
      <wh-icon name="caret-right-circle"></wh-icon>
      <br />
      ${msg("Back to top")}
    </button>`;
  }

  static styles = css`
    :host {
      --web-kiosk-text-color: #323264;
    }

    button {
      display: none;
      font-size: 16px;
      color: var(--web-kiosk-text-color, hotpink);
      background: none;
      border: none;
      cursor: pointer;
    }

    .visible {
      display: flex;
      align-items: center;
      flex-direction: column;
    }

    wh-icon {
      font-size: 28px;
      transform: rotate(-90deg);
    }
  `;
}
